import { useState } from "react";
import { Clock, Loader2, Play, Save } from "lucide-react";
import { Button, Input, Switch, TimezoneCombobox } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  runAutomationNow,
  saveAutomation,
  type AutomationSettings,
  type AutomationView,
} from "../../../../lib/marketingAutomation";
import { Field, Note, Panel, dt } from "../shared";

/** Clamp a typed number to the range the server accepts, so the form cannot
 *  offer a value that comes back as a 400. */
function bounded(raw: string, min: number, max: number, fallback: number): number {
  const n = Number(raw.replace(/\s/g, ""));
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * The clock: whether it runs, how often, and how much it may spend.
 *
 * THE RUN NOW BUTTON WORKS WITH THE SWITCH OFF. That is the point of it:
 * watching one batch happen is exactly what somebody wants to do BEFORE
 * committing to letting it happen unattended. It overrides the switch and the
 * interval and nothing else - the hard stop, the ramp, the caps, quiet hours
 * and the circuit breaker all still apply, because those belong to the sender
 * rather than to the clock.
 *
 * THE SWITCH IS THIS ROW AND NOT AN ENVIRONMENT VARIABLE. The site automation
 * module was gated on a variable as well as on its settings row, and the result
 * was a switch in this interface that did nothing in production because the
 * variable was never set.
 */
export function ScheduleCard({
  view,
  canManage,
  onChanged,
}: {
  view: AutomationView;
  canManage: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const [draft, setDraft] = useState<AutomationSettings>(view.settings);
  const [busy, setBusy] = useState(false);
  const [ran, setRan] = useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(view.settings);
  const set = (patch: Partial<AutomationSettings>) => setDraft((d) => ({ ...d, ...patch }));

  async function save() {
    setBusy(true);
    try {
      await saveAutomation(draft);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function runNow() {
    setBusy(true);
    setRan(false);
    try {
      await runAutomationNow();
      setRan(true);
      // The tick is detached on the server, so the history needs a moment
      // before it has anything new to show.
      setTimeout(onChanged, 1200);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title={t("marketing.auto.scheduleTitle")}
      description={t("marketing.auto.scheduleIntro")}
      icon={<Clock className="size-4" />}
    >
      <label className="flex items-start gap-2.5 text-sm">
        <Switch
          checked={draft.enabled}
          onCheckedChange={(v) => set({ enabled: v })}
          disabled={!canManage || busy}
          className="mt-0.5"
        />
        <span>
          {t("marketing.auto.enabled")}
          <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
            {t("marketing.auto.enabledHint")}
          </span>
        </span>
      </label>

      <div className="mt-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("marketing.auto.interval")} hint={t("marketing.auto.intervalHint")}>
          <Input
            value={String(draft.intervalMinutes)}
            onChange={(e) => set({ intervalMinutes: bounded(e.target.value, 5, 1440, draft.intervalMinutes) })}
            inputMode="numeric"
            disabled={!canManage || busy}
          />
        </Field>
        <Field label={t("marketing.auto.perTick")} hint={t("marketing.auto.perTickHint")}>
          <Input
            value={String(draft.campaignsPerTick)}
            onChange={(e) => set({ campaignsPerTick: bounded(e.target.value, 1, 10, draft.campaignsPerTick) })}
            inputMode="numeric"
            disabled={!canManage || busy}
          />
        </Field>
        <Field label={t("marketing.auto.globalCap")} hint={t("marketing.auto.globalCapHint")}>
          <Input
            value={String(draft.globalDailyCap)}
            onChange={(e) => set({ globalDailyCap: bounded(e.target.value, 1, 20000, draft.globalDailyCap) })}
            inputMode="numeric"
            disabled={!canManage || busy}
          />
        </Field>
        <Field label={t("marketing.auto.timezone")} hint={t("marketing.auto.timezoneHint")}>
          <TimezoneCombobox
            value={draft.timezone}
            onChange={(timezone) => set({ timezone })}
            disabled={!canManage || busy}
            aria-label={t("marketing.auto.timezone")}
            searchPlaceholder={t("common.timezoneSearch")}
            emptyLabel={t("common.timezoneNone")}
          />
        </Field>
      </div>

      <label className="mt-3.5 flex items-start gap-2.5 border-t border-border pt-3.5 text-sm">
        <Switch
          checked={draft.warmupEnabled}
          onCheckedChange={(v) => set({ warmupEnabled: v })}
          disabled={!canManage || busy}
          className="mt-0.5"
        />
        <span>
          {t("marketing.auto.warmup")}
          <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">
            {t("marketing.auto.warmupHint")}
          </span>
        </span>
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3.5">
        <Button onClick={save} disabled={!canManage || busy || !dirty}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t("marketing.auto.save")}
        </Button>
        <Button variant="outline" onClick={runNow} disabled={!canManage || busy || view.stop.stopped}>
          <Play className="size-4" />
          {t("marketing.auto.runNow")}
        </Button>
        <span className="text-xs text-muted-foreground">
          {view.nextDueAt
            ? t("marketing.auto.nextDue", { when: dt(view.nextDueAt) })
            : t("marketing.auto.neverRan")}
        </span>
      </div>

      {ran ? <Note tone="info" className="mt-3">{t("marketing.auto.ranNote")}</Note> : null}
    </Panel>
  );
}
